import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { UUID } from "crypto";
import api from "@/app/api";
import { lotKeys } from "../keys/lots";
import type { LotCreate, LotUpdate } from "@/app/api/lots/client";
import type { LotResponse } from "@/app/api/types/lot";
import { useLots as useLotsV2 } from "@stockline/web";
import type { Lot as OfflineLot } from "@stockline/shared/types/inventory/lot";

/**
 * V2 Dexie Lot → legacy snake_case `LotResponse`. Single-source of the
 * shape conversion so call sites that read `lot.lot_number` / `lot.tracking_number`
 * / `lot.supplier_id` keep working unchanged. The `supplier` relation is
 * intentionally not stitched — list consumers don't read it (only the
 * `useLotFull(id)` singleton path does, and that stays on REST until the
 * V2 partner join is wired through here).
 */
function mapV2LotToLegacy(lot: OfflineLot): LotResponse {
  return {
    id: lot.id as UUID,
    company_id: lot.companyId as UUID,
    supplier_id: lot.supplierId == null ? null : (lot.supplierId as UUID),
    lot_number: lot.lotNumber,
    tracking_number: lot.trackingNumber,
    status: lot.status,
    date: lot.date,
    lot_metadata: lot.lotMetadata,
    created_at: lot.createdAt,
    updated_at: lot.updatedAt,
  };
}

/**
 * List of lots for the current company. Dexie-backed via `@stockline/web`;
 * the legacy REST call (`api.lots.list()`) is gone — SyncEngine keeps the
 * `lots` table fresh through full + delta + SSE. Returns the same
 * `{ data, isLoading, ... }` shape and snake_case `LotResponse` rows that
 * every call site already expects, so no caller changes.
 *
 * `isLoading` is always `false` once `AISyncProvider` has mounted the lots
 * resource — the live query resolves synchronously from Dexie. Genuine
 * cold-start (Dexie not yet hydrated) shows as `data === []`, which every
 * caller already handles (default `lots = []`).
 */
export function useLots() {
  const offlineLots = useLotsV2();
  const data = useMemo(() => offlineLots.map(mapV2LotToLegacy), [offlineLots]);
  return {
    data,
    isLoading: false,
    isFetching: false,
    isError: false,
    error: null as Error | null,
    refetch: async () => ({ data }),
  } as const;
}

export function useLot(id: UUID | null) {
  return useQuery({
    queryKey: lotKeys.detail(id!),
    queryFn: () => api.lots.get(id!),
    enabled: !!id,
  });
}

export function useLotFull(id: UUID | null) {
  return useQuery({
    queryKey: lotKeys.full(id!),
    queryFn: () => api.lots.getFull(id!),
    enabled: !!id,
  });
}

// Lot mutations
export function useCreateLot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LotCreate) => api.lots.create(data),
    onSuccess: () => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: lotKeys.all });
      queryClient.invalidateQueries({ queryKey: ["products", "global"] });
      // Also invalidate stocks in case lot creation affected stock
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
    },
  });
}

export function useUpdateLot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: LotUpdate }) =>
      api.lots.update(id, data),
    onSuccess: (lot) => {
      queryClient.invalidateQueries({ queryKey: lotKeys.all });
      queryClient.invalidateQueries({ queryKey: lotKeys.detail(lot.id) });
      queryClient.invalidateQueries({ queryKey: ["products", "global"] });
    },
  });
}

export function useDeleteLot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.lots.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: lotKeys.all });
      queryClient.invalidateQueries({ queryKey: ["products", "global"] });
    },
  });
}

export function useAdjustLot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lotId,
      quantity,
      reason,
    }: {
      lotId: UUID;
      quantity: number;
      reason?: string;
    }) =>
      api.lots.adjust(lotId, {
        lot_id: lotId,
        quantity,
        flow_type: quantity > 0 ? "IN" : "OUT",
        reason,
      }),
    onSuccess: (lot) => {
      queryClient.invalidateQueries({ queryKey: lotKeys.all });
      queryClient.invalidateQueries({ queryKey: lotKeys.detail(lot.id) });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      queryClient.invalidateQueries({ queryKey: ["products", "global"] });
    },
  });
}

export function useLotFlows(lotId: UUID | null) {
  return useQuery({
    queryKey: lotKeys.flows(lotId!),
    queryFn: async () => {
      try {
        return await api.lots.getFlows(lotId!);
      } catch (error: any) {
        // Gracefully handle missing endpoint until backend exposes it
        if (error?.status === 404) return [];
        throw error;
      }
    },
    enabled: !!lotId,
  });
}

// Lot document hooks
export function useLotDocuments(lotId: UUID | null) {
  return useQuery({
    queryKey: [...lotKeys.detail(lotId!), "documents"],
    queryFn: () => api.lots.getDocuments(lotId!),
    enabled: !!lotId,
  });
}

export function useUploadLotDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lotId, files }: { lotId: UUID; files: File[] }) =>
      api.lots.uploadDocuments(lotId, files),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...lotKeys.detail(variables.lotId), "documents"],
      });
      queryClient.invalidateQueries({
        queryKey: lotKeys.detail(variables.lotId),
      });
    },
  });
}

export function useDownloadLotDocument() {
  return useMutation({
    mutationFn: ({ lotId, fileUrl }: { lotId: UUID; fileUrl: string }) =>
      api.lots.downloadDocument(lotId, fileUrl),
    onSuccess: (blob, variables) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = variables.fileUrl.split("/").pop() || "document";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

export function useRemoveLotDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lotId, fileUrl }: { lotId: UUID; fileUrl: string }) =>
      api.lots.removeDocument(lotId, fileUrl),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...lotKeys.detail(variables.lotId), "documents"],
      });
      queryClient.invalidateQueries({
        queryKey: lotKeys.detail(variables.lotId),
      });
    },
  });
}

export function useRenameLotDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lotId,
      fileUrl,
      newName,
    }: {
      lotId: UUID;
      fileUrl: string;
      newName: string;
    }) => api.lots.renameDocument(lotId, fileUrl, newName),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...lotKeys.detail(variables.lotId), "documents"],
      });
      queryClient.invalidateQueries({
        queryKey: lotKeys.detail(variables.lotId),
      });
    },
  });
}
